/**
 * Sources Section
 *
 * Displays source attribution table showing traffic sources and landing pages.
 */

import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getSourceAttribution } from '../../services/analytics'
import { SourceAttributionResponse } from '../../types/analytics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'

interface SourcesSectionProps {
    from: string
    to: string
}

const SourcesSection: React.FC<SourcesSectionProps> = ({ from, to }) => {
    const { t } = useTranslation()

    const { data, renderState } =
        useQueryWithHandler<SourceAttributionResponse>({
            queryKey: [QUERY_KEYS.LIVECHAT_ANALYTICS_SOURCES, from, to],
            queryFn: () => getSourceAttribution({ from, to }),
        })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const rows = data?.results || []

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">
                    {t('Source Attribution')}
                </CardTitle>
                {data?.totalRooms && (
                    <span className="text-sm text-muted-foreground">
                        {t('{{count}} total conversations', {
                            count: data.totalRooms,
                        })}
                    </span>
                )}
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('Source Domain')}</TableHead>
                            <TableHead>{t('Landing Page')}</TableHead>
                            <TableHead className="text-right">
                                {t('Conversations')}
                            </TableHead>
                            <TableHead className="text-right w-20">%</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length > 0 ? (
                            rows.map((row) => (
                                <TableRow
                                    key={`${row.origin}-${row.landingPage}`}
                                >
                                    <TableCell className="font-medium">
                                        {row.origin || t('Direct')}
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className="text-blue-600 cursor-pointer hover:underline truncate block max-w-[300px]"
                                            title={row.landingPage}
                                            onClick={() => {
                                                const url =
                                                    row.landingPage.startsWith(
                                                        '/'
                                                    )
                                                        ? `${row.origin}${row.landingPage}`
                                                        : row.landingPage
                                                window.open(url, '_blank')
                                            }}
                                        >
                                            {row.landingPage}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        {(row.roomCount ?? 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {(row.percentage ?? 0).toFixed(1)}%
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {t('No source data available')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    )
}

export default SourcesSection
